import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["toggle"]

  connect() {
    console.log('Class inspector controller connected!');
    // Temporary test to ensure controller is working
    window.classInspectorLoaded = true;

    // Clear localStorage state on fresh load to reset
    localStorage.removeItem('classInspectorActive')
    console.log('Cleared localStorage state');

    // Initialize as disabled
    this.updateToggleState(false)

    // Set up the toggle button click handler
    const toggleButton = document.getElementById('class-inspector-toggle')
    if (toggleButton) {
      toggleButton.addEventListener('click', this.toggle.bind(this))
      console.log('Toggle button event listener added');
    } else {
      console.log('Toggle button not found!');
    }

    // Ensure click mode is disabled on start
    this.disableClickMode()
  }

  disconnect() {
    // Clean up event listeners and disable click mode
    this.disableClickMode()
    this.removeAllBadges()
  }

  toggle() {
    console.log('Toggle clicked!');

    // Check current state by looking at the body class instead of localStorage
    const isCurrentlyActive = document.body.classList.contains('class-inspector-click-mode')
    console.log('Current state (from body class):', isCurrentlyActive);

    if (isCurrentlyActive) {
      console.log('Disabling click mode');
      this.disableClickMode()
      localStorage.setItem('classInspectorActive', 'false')
      this.updateToggleState(false)
    } else {
      console.log('Enabling click mode');
      this.enableClickMode()
      localStorage.setItem('classInspectorActive', 'true')
      this.updateToggleState(true)
    }
  }

  enableClickMode() {
    console.log('Adding click mode class to body');
    document.body.classList.add('class-inspector-click-mode')
    this.handleElementClick = this.handleElementClick.bind(this)
    document.addEventListener('click', this.handleElementClick, true)
    console.log('Click mode enabled - cursor should be crosshair');
  }

  disableClickMode() {
    console.log('Disabling click mode and removing body class');
    document.body.classList.remove('class-inspector-click-mode')
    this.removeAllBadges()
    if (this.handleElementClick) {
      document.removeEventListener('click', this.handleElementClick, true)
      console.log('Removed click event listener');
    }
  }

  handleElementClick(event) {
    console.log('Element clicked!', event.target);

    // Prevent default action for inspector clicks
    event.preventDefault()
    event.stopPropagation()

    const element = event.target

    // Skip if clicking on a badge or the toggle button
    if (element.classList.contains('class-inspector-badge') ||
        element.closest('.class-inspector-badge') ||
        element.id === 'class-inspector-toggle' ||
        element.closest('#class-inspector-toggle')) {
      console.log('Skipping badge or toggle button');
      return
    }

    console.log('Processing element:', element.tagName, element.className);

    // Remove any existing badges first
    this.removeAllBadges()

    // Check if this element should show badge info
    if (this.shouldShowBadgeForElement(element)) {
      console.log('Creating badge for element');
      this.createBadgeForElement(element)
      // Auto-copy the information when clicking on an element
      this.copyElementInfo(element)
    } else {
      console.log('Element does not qualify for badge');
    }
  }

  shouldShowBadgeForElement(element) {
    console.log('Checking element for badge:', element.tagName, element.className);

    // Show badge for ANY element - we'll always show at least the element type
    return true
  }

  updateToggleState(isActive) {
    const toggleButton = document.getElementById('class-inspector-toggle')
    if (toggleButton) {
      if (isActive) {
        toggleButton.classList.remove('btn-outline-secondary')
        toggleButton.classList.add('btn-secondary')
        toggleButton.title = 'Disable Class Inspector (Click Mode)'
      } else {
        toggleButton.classList.remove('btn-secondary')
        toggleButton.classList.add('btn-outline-secondary')
        toggleButton.title = 'Enable Class Inspector (Click Mode)'
      }
    }
  }

  filterRelevantClasses(classes) {
    // Only keep text-related classes
    const textClassPatterns = [
      /^text-/,              // text utilities (text-primary, text-center, text-lg, etc.)
      /^font-/,              // font utilities (font-bold, font-size, etc.)
      /^fw-/,                // font weight (fw-bold, fw-normal, etc.)
      /^fs-/,                // font size (fs-1, fs-2, etc.)
      /^lh-/,                // line height (lh-1, lh-base, etc.)
      /^display-/,           // display typography (display-1, display-2, etc.)
      /^lead$/,              // lead class
      /^small$/,             // small class
      /^mark$/,              // mark class
      /^del$/,               // del class
      /^s$/,                 // strikethrough
      /^ins$/,               // inserted text
      /^sub$/,               // subscript
      /^sup$/,               // superscript
    ]

    return classes.filter(className => {
      return textClassPatterns.some(pattern => pattern.test(className))
    }).slice(0, 3) // Limit to 3 most relevant text classes
  }

  createBadgeForElement(element) {
    console.log('Creating badge for:', element.tagName, element.className);

    const badge = document.createElement('div')
    badge.className = 'class-inspector-badge'

    // Get component name and relevant classes
    const componentName = element.dataset.component
    const classes = Array.from(element.classList)
    const relevantClasses = this.filterRelevantClasses(classes)
    const classText = relevantClasses.join(' ')
    const elementType = element.tagName.toLowerCase()

    // Get component partial filename using smart detection
    const partialName = this.detectPartialFromElement(element)
    console.log('Detected partial:', partialName, 'for element:', element);

    // Create formatted content - always show something
    let badgeContent = ''

    // Always show partial name if detected
    if (partialName) {
      badgeContent = `<span style="color: #ff6b6b; font-weight: 700; font-size: 10px;">${partialName}</span><br>`
    }

    if (componentName) {
      badgeContent += `<span style="color: #ffc107; font-weight: 700;">[${componentName}]</span>`
      if (classText) {
        badgeContent += ` <span style="color: #ffffff;">${classText}</span>`
      }
    } else if (classText) {
      badgeContent += `<span style="color: #90EE90; font-weight: 700;">&lt;${elementType}&gt;</span> <span style="color: #ffffff;">${classText}</span>`
    } else {
      // Show just the element type if no component or relevant classes
      badgeContent += `<span style="color: #90EE90; font-weight: 700;">&lt;${elementType}&gt;</span> <span style="color: #ccc;">no text classes</span>`
    }

    // Add copied indicator
    badgeContent += ` <span style="color: #28a745; font-size: 10px; margin-left: 5px;">📋 Copied!</span>`

    badge.innerHTML = badgeContent

    // Position the badge near the element
    const rect = element.getBoundingClientRect()
    const topPosition = rect.top + window.scrollY - 35
    const leftPosition = rect.left + window.scrollX + 5

    badge.style.position = 'absolute'
    badge.style.top = topPosition + 'px'
    badge.style.left = leftPosition + 'px'
    badge.style.zIndex = '99999' // Even higher z-index
    badge.style.display = 'block' // Ensure it's displayed
    badge.style.visibility = 'visible' // Ensure it's visible
    badge.style.opacity = '1' // Override any opacity issues

    console.log('Positioning badge at:', topPosition, leftPosition);
    document.body.appendChild(badge)
    console.log('Badge created and added to DOM. Element rect:', rect);

    // Store reference for cleanup
    element.dataset.classInspectorActive = 'true'
    badge.setAttribute('data-for-element', this.getElementId(element))
  }

  copyElementInfo(element) {
    // Get component name and relevant classes
    const componentName = element.dataset.component
    const classes = Array.from(element.classList)
    const relevantClasses = this.filterRelevantClasses(classes)
    const classText = relevantClasses.join(' ')
    const elementType = element.tagName.toLowerCase()

    // Get component partial filename using smart detection
    const partialName = this.detectPartialFromElement(element)

    // Build copy text
    let copyText = ''

    // Always show partial name if detected
    if (partialName) {
      copyText += `Detected partial: ${partialName}\n`
    }

    if (componentName) {
      const componentText = `[${componentName}]`
      copyText += `Creating badge for: ${componentText}`
      if (classText) {
        copyText += ` ${classText}`
      }
    } else if (classText) {
      const elementText = `<${elementType}>`
      copyText += `Creating badge for: ${elementText} ${classText}`
    } else {
      // Show just the element type if no component or relevant classes
      const elementText = `<${elementType}>`
      copyText += `Creating badge for: ${elementText} no text classes`
    }

    // Copy to clipboard
    navigator.clipboard.writeText(copyText).then(() => {
      console.log('Copied to clipboard:', copyText)
    }).catch(err => {
      console.error('Failed to copy:', err)
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = copyText
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      console.log('Copied to clipboard (fallback):', copyText)
    })
  }

  getPartialName(componentName) {
    // Map component data attributes to their partial filenames
    const componentPartialMap = {
      'hero': '_hero.html.haml',
      'feature-grid': '_feature_grid.html.haml',
      'main-navigation': '_navigation.html.haml',
      'navigation': '_navigation.html.haml',
      'footer': '_footer.html.haml',
      'cta': '_cta.html.haml',
      'cta-section': '_cta_section.html.haml',
      'testimonial': '_testimonial.html.haml',
      'pricing-table': '_pricing_table.html.haml',
      'feature-card': '_feature_card.html.haml',
      'image-left-text-right': '_image_left_text_right.html.haml',
      'image-right-text-left': '_image_right_text_left.html.haml',
      'video-section': '_video_section.html.haml',
      'contact-form': '_contact_form.html.haml',
      'tool-header': '_tool_header.html.haml',
      'content-block': '_content_block.html.haml',
      'image-text': '_image_text.html.haml',
      'all-products-grid': '_all_products_grid.html.haml',
      'adoption-products-grid': '_adoption_products_grid.html.haml'
    }

    return componentPartialMap[componentName] || null
  }

  detectPartialFromElement(element) {
    // First check if element or any parent has data-component
    let current = element
    while (current && current !== document.body) {
      if (current.dataset && current.dataset.component) {
        return this.getPartialName(current.dataset.component)
      }
      current = current.parentElement
    }

    // If no data-component found, analyze CSS classes to guess component
    current = element
    while (current && current !== document.body) {
      const classes = Array.from(current.classList || [])
      const partialGuess = this.guessPartialFromClasses(classes, current)
      if (partialGuess) {
        return partialGuess
      }
      current = current.parentElement
    }

    return null
  }

  guessPartialFromClasses(classes, element) {
    // Look for component-specific class patterns
    const patterns = {
      'hero': /^(hero|banner|jumbotron)$/i,
      'feature-grid': /^(feature-grid|features)$/i,
      'navbar': /^(navbar|navigation)$/i,
      'footer': /^(footer)$/i,
      'cta': /^(cta|call-to-action)$/i,
      'contact': /^(contact)$/i,
      'testimonial': /^(testimonial|review)$/i,
      'pricing': /^(pricing|price)$/i,
      'card': /^(card|feature-card)$/i
    }

    for (const className of classes) {
      for (const [component, pattern] of Object.entries(patterns)) {
        if (pattern.test(className)) {
          return this.getPartialName(component) || `_${component}.html.haml`
        }
      }
    }

    // Check if element is in a section with specific structure
    if (element.tagName === 'SECTION' || element.closest('section')) {
      const section = element.tagName === 'SECTION' ? element : element.closest('section')

      // Look for hero-like patterns
      if (section.querySelector('h1, .display-1, .display-2, .display-3')) {
        const hasButton = section.querySelector('.btn, button')
        const hasLargeText = section.querySelector('.lead, .display-1, .display-2, .display-3')
        if (hasButton && hasLargeText) {
          return '_hero.html.haml'
        }
      }

      // Look for feature grid patterns
      if (section.querySelectorAll('.card, .feature').length >= 2) {
        return '_feature_grid.html.haml'
      }
    }

    return null
  }

  getElementId(element) {
    // Create a unique identifier for the element
    if (element.id) return element.id
    if (element.className) return element.className.split(' ')[0]
    return element.tagName.toLowerCase() + '-' + Array.from(element.parentNode.children).indexOf(element)
  }

  removeAllBadges() {
    const badges = document.querySelectorAll('.class-inspector-badge')
    badges.forEach(badge => badge.remove())

    // Clean up data attributes
    const elements = document.querySelectorAll('[data-class-inspector-active]')
    elements.forEach(element => {
      delete element.dataset.classInspectorActive
    })
  }
}